import { computed, makeAutoObservable } from 'mobx';
import { AppState } from 'src/AppState/AppState';
import { MobxMapAutoNew } from 'src_common/common/mobx-utils/MobxMapAutoNew';
import { timeout } from 'src_common/common/mobx-utils/timeout';
import { MarketModel } from 'src_common/common/websocket2/models/MarketModel';
import { TermsModelType } from 'src_common/common/websocket2/modelsApi/Market';
import { EventResultsState } from 'src/domains/sportsbook/trading/view/eventDetails/eventResults/EventResults.state';
import { EventResultsSettingsSelectionState } from './EventResultsSettingsSelectionState';
import { EventId, MarketId, SelectionId } from 'src_common/common/websocket2/id/WebsocketId';

export type SelectionsGroupType =
    | {
          type: 'hide';
      }
    | {
          type: 'disable';
      }
    | {
          type: 'enable';
          onClick: () => void;
      };

const groupSelections = (
    selections: Array<EventResultsSettingsSelectionState>,
    onClick: (list: Array<EventResultsSettingsSelectionState>) => void
): SelectionsGroupType => {
    if (selections.length > 0) {
        const unlock = selections.filter((item) => item.unlock);

        if (unlock.length > 0) {
            return {
                type: 'enable',
                onClick: (): void => {
                    onClick(unlock);
                },
            };
        }

        return {
            type: 'disable',
        };
    }

    return {
        type: 'hide',
    };
};

export class EventResultsSettingsState {
    private readonly selectionsMap: MobxMapAutoNew<SelectionId, EventResultsSettingsSelectionState>;

    public constructor(
        private readonly appState: AppState,
        private readonly eventResultsState: EventResultsState,
        public readonly eventId: EventId,
        public readonly marketId: MarketId
    ) {
        this.selectionsMap = new MobxMapAutoNew((selectionId: SelectionId) => {
            return new EventResultsSettingsSelectionState(this.appState, this.eventId, this.marketId, selectionId);
        });
        makeAutoObservable(this);
    }

    private get marketModel(): MarketModel | null {
        return this.marketId.getModel();
    }

    private get selectionsAll(): Array<EventResultsSettingsSelectionState> | null {
        const marketModel = this.marketModel;

        if (marketModel === null) {
            return null;
        }

        return marketModel.selectionsIds2.map((selectionId) => this.selectionsMap.get(selectionId));
    }

    public get selections(): Array<EventResultsSettingsSelectionState> {
        const selections = this.selectionsAll ?? [];
        if (this.eventResultsState.shouldHideLosers) {
            return selections.filter((item) => !item.isLost);
        }

        return selections;
    }

    public get isPlace(): boolean {
        for (const model of this.selections) {
            if (model.isPlace) {
                return true;
            }
        }

        return false;
    }

    public get isSp(): boolean {
        return this.marketModel?.sp ?? false;
    }

    public get eachWay(): Array<TermsModelType> {
        const eachWay = this.marketModel?.eachWay?.terms ?? [];
        return eachWay;
    }

    private setLoading(selections: Array<EventResultsSettingsSelectionState>, message: string | null): void {
        for (const selection of selections) {
            selection.loading = message;
        }
    }

    @computed public get selectionsForSave(): SelectionsGroupType {
        const selections = this.selections.filter((selection) => selection.getSelectionStatus() === 'to-be-saved');

        if (selections.filter((item) => item.unlock).length < 2) {
            return {
                type: 'disable',
            };
        }

        return groupSelections(
            selections,
            async (selections: Array<EventResultsSettingsSelectionState>): Promise<void> => {
                this.setLoading(selections, 'Saving');

                try {
                    for (const selection of selections) {
                        await selection.savePrice();
                    }
                } catch (error) {
                    console.error(error);
                    this.setLoading(selections, null);
                    return;
                }

                const data = selections.map((selection) => ({
                    id: selection.selectionId.toOldId(),
                    result: selection.getDataToSave(true),
                }));

                try {
                    const result = await this.appState.sdkStaff.selection_result_group({
                        requestBody: {
                            event_id: this.eventId.toOldId(),
                            market_id: this.marketId.toOldId(),
                            data,
                        },
                    });

                    if (this.appState.reduxAction.addErrorEventChange(result)) {
                        this.setLoading(selections, null);
                        return;
                    }
                } catch (error) {
                    console.error(error);
                    this.setLoading(selections, null);
                    return;
                }

                await timeout(1500);

                for (const selection of selections) {
                    selection.resetUserValues();
                }
                this.setLoading(selections, null);
            }
        );
    }

    @computed public get isMarketSettled(): boolean {
        const allSelections = this.selectionsAll;
        if (allSelections === null || allSelections.length === 0) {
            return false;
        }

        const settledSelections = allSelections.filter((sel) => sel.isSelectionSettled === true);

        return settledSelections.length === allSelections.length;
    }

    @computed public get selectionsForSettle(): SelectionsGroupType {
        if (this.isMarketSettled) {
            return {
                type: 'disable',
            };
        }

        const allSelections = this.selectionsAll;

        if (allSelections === null) {
            return {
                type: 'disable',
            };
        }

        const selections = this.selections.filter((selection) => selection.getSelectionStatus() === 'to-be-settled');
        if (selections.filter((item) => item.unlock).length < 2) {
            return {
                type: 'disable',
            };
        }

        return groupSelections(
            selections,
            async (selections: Array<EventResultsSettingsSelectionState>): Promise<void> => {
                if (selections.length === allSelections.length) {
                    this.setLoading(selections, 'Settling');

                    try {
                        const result = await this.appState.sdkStaff.staff_selection_settle_group({
                            requestBody: {
                                event_id: this.eventId.toOldId(),
                                market_id: this.marketId.toOldId(),
                            },
                        });

                        if (this.appState.reduxAction.addErrorEventChange(result)) {
                            this.setLoading(selections, null);
                            return;
                        }
                    } catch (error) {
                        console.error(error);
                        this.setLoading(selections, null);
                        return;
                    }

                    await timeout(1500);

                    for (const selection of selections) {
                        selection.resetUserValues();
                    }

                    this.setLoading(selections, null);
                } else {
                    for (const selection of selections) {
                        selection.settle().catch((error) => {
                            console.error(error);
                        });

                        selection.resetUserValues();
                    }
                }
            }
        );
    }

    @computed public get selectionsForUnlock(): SelectionsGroupType {
        const toUnlockSelections = this.selections.filter((selection) => selection.unlock === false);

        if (toUnlockSelections.length > 0) {
            return {
                type: 'enable',
                onClick: (): void => {
                    for (const selection of toUnlockSelections) {
                        selection.setUnlock(true);
                    }
                },
            };
        } else {
            return {
                type: 'disable',
            };
        }
    }

    @computed public get selectionsForUnsettle(): SelectionsGroupType {
        const selections = this.selectionsAll;
        const hideGroup: SelectionsGroupType = { type: 'hide' };

        if (selections === null) {
            return hideGroup;
        }

        const unsettleGroup: SelectionsGroupType = {
            type: 'enable',
            onClick: (): void => {
                const selectionIds = selections.map((selection) => selection.selectionId);
                this.appState.sdkStaff
                    .selectionsUnsettleGroup({
                        requestBody: {
                            event_id: this.eventId.toOldId(),
                            market_id: this.marketId.toOldId(),
                            selections: selectionIds.map((id) => id.toOldId()),
                        },
                    })
                    .catch((e) => console.error(e));
            },
        };

        if (this.isMarketSettled === false) {
            return hideGroup;
        }

        return unsettleGroup;
    }

    @computed public get selectionsForSetEmptyAsVoid(): SelectionsGroupType {
        const selections = this.selections.filter(
            (selection) => selection.resultInput.value === '' || selection.resultInput.value === 'unresult'
        );

        if (selections.length > 0) {
            return {
                type: 'enable',
                onClick: (): void => {
                    for (const selection of selections) {
                        selection.resultInput.setValue('void');
                    }
                },
            };
        }

        return {
            type: 'disable',
        };
    }

    @computed public get selectionsForSetEmptyAsLost(): SelectionsGroupType {
        const selections = this.selections.filter(
            (selection) => selection.resultInput.value === '' || selection.resultInput.value === 'unresult'
        );

        if (selections.length > 0) {
            return {
                type: 'enable',
                onClick: (): void => {
                    for (const selection of selections) {
                        selection.resultInput.setValue('lost');
                    }
                },
            };
        }

        return {
            type: 'disable',
        };
    }
}
